import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DragIndicator, FlagRounded, PlayArrowRounded } from '@mui/icons-material';
import { Box } from '@mui/material';
import { FC, Fragment, ReactNode, useCallback, useEffect, useState } from 'react';
import datetimeDecorator from 'src/decorators/datetime.decorator';
import { DispatchCard } from 'src/entities/dispatch';

import { ExpandedRowVM, ExpandedStopVM } from './buildExpandedRowVM';
import { ExpandedStopRow } from './ExpandedStopRow';
import { formatPlanRange } from './planRangeText';
import { reorderByKey } from './reorderByKey';

type Props = {
  vm: ExpandedRowVM;
  /** stop から集約カードを解決するための元配列。VM の stop は operation 単位でカードを持たない */
  cards: readonly DispatchCard[];
  /**
   * 停車地行のクリック。未指定なら行はクリックできない。
   * 渡すのは表示番号の seriesIndex ではなく dispatchOrderIndex（既存 DispatchSelection が
   * 期待する集約カード順）。vehicle はこの層に無いので上位が足す。
   */
  onSelectCard?: (card: DispatchCard, dispatchOrderIndex: number) => void;
  /**
   * 停車地を並べ替えたときに呼ぶ。未指定なら並べ替えできない（ハンドルも出さない）。
   * 渡すのは並べ替え後の stop 全体で、保存する id 列の組み立ては呼び出し側が行う。
   */
  onReorderStops?: (stops: ExpandedStopVM[]) => void;
  /** 指定時はハンドルを disabled にし、素の title 属性で理由を出す */
  reorderDisabledReason?: string;
  /** その車両で新しく入った案件 ID。停車地ごとの判定に使う */
  newlyAssignedOrderIds?: ReadonlySet<number>;
};

const hhmm = (date: Date | null) => (date ? datetimeDecorator.toHourMinutes(date) ?? '--:--' : '--:--');

/**
 * 停車地の住所のフォールバック。地点が住所を持たないとき（legRun を引けない operation）
 * だけ集約カードから引く。通常は stop.address（停車ごとの実データ）を使う。
 *
 * 積地の集約カードは LoadingCard で、住所は `address` に入る（`originAddress` は
 * DeliveryCard 側のフィールド）。type で弾くと積地の住所が丸ごと出なくなる。
 */
function addressOf(card: DispatchCard | undefined, kind: ExpandedStopVM['kind']): string | undefined {
  if (!card) return undefined;
  if (card.type === 'loading') return card.address;
  return kind === 'loading' ? card.originAddress : card.storeAddress;
}

// 静的な modifier。レンダリングのたびに新しい配列を作らないようコンポーネント外に置く
const dndModifiers = [restrictToVerticalAxis];

/**
 * 並べ替え可能な停車地行。掴めるのは左端のハンドルだけ。
 * 行全体を掴めるようにすると、停車地を選ぼうとしただけでドラッグが始まる
 * （車両行の SortableVehicleGroup と同じ方針）。
 */
const SortableStopRow: FC<{
  id: string;
  disabledReason?: string;
  children: (dragHandle: ReactNode) => ReactNode;
}> = ({ id, disabledReason, children }) => {
  const disabled = !!disabledReason;
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id, disabled });

  const handle = (
    <Box
      component="button"
      type="button"
      aria-label="停車地の並べ替え"
      disabled={disabled}
      // disabled 要素は MUI Tooltip の hover を拾えないので素の title で出す
      title={disabledReason}
      {...(disabled ? {} : attributes)}
      {...(disabled ? {} : listeners)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 12,
        flexShrink: 0,
        alignSelf: 'stretch',
        border: 'none',
        p: 0,
        bgcolor: 'transparent',
        color: 'hsl(var(--muted-foreground))',
        opacity: disabled ? 0.25 : 0.5,
        cursor: disabled ? 'default' : 'grab',
        '&:active': disabled ? undefined : { cursor: 'grabbing' },
        '&:hover': disabled ? undefined : { opacity: 1 },
      }}
    >
      <DragIndicator sx={{ fontSize: 12 }} />
    </Box>
  );

  return (
    <Box ref={setNodeRef} style={{ transform: CSS.Transform.toString(transform), transition }}>
      {children(handle)}
    </Box>
  );
};

/**
 * 出発地 / 帰着地の行（サンプル route-planner の TerminalRow 準拠）。
 * 停車地の緑・青と競合させないようニュートラル配色にする。
 */
const TerminalRow: FC<{ variant: 'start' | 'end'; locationName: string; time: string; address?: string }> = ({
  variant, locationName, time, address,
}) => {
  const isStart = variant === 'start';
  const label = isStart ? '出発地' : '帰着地';
  const Icon = isStart ? PlayArrowRounded : FlagRounded;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        px: 1.25,
        py: 0.75,
        bgcolor: 'hsl(var(--muted) / 0.6)',
        ...(isStart
          ? { borderBottom: '1px solid hsl(var(--border) / 0.35)' }
          : { borderTop: '1px solid hsl(var(--border) / 0.35)' }),
      }}
    >
      {/* 停車地行のハンドル位置ぶん空けて、本文の開始位置を揃える */}
      <Box sx={{ width: 12, flexShrink: 0 }} aria-hidden="true" />
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', pt: '2px', flexShrink: 0 }}>
        <Box component="span" sx={{ fontSize: 10, fontFamily: 'monospace', color: 'hsl(var(--muted-foreground))' }}>
          {time}
        </Box>
        <Box
          data-testid={`terminal-icon-${variant}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 18,
            height: 18,
            borderRadius: '9999px',
            bgcolor: 'hsl(var(--foreground))',
            color: 'hsl(var(--background))',
          }}
          title={label}
        >
          <Icon sx={{ fontSize: 12 }} />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* 名称が 2 行になっても種別バッジを 1 行目の高さに置く */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
          <Box
            component="span"
            sx={{
              flexShrink: 0,
              whiteSpace: 'nowrap',
              px: 0.5,
              py: '1px',
              borderRadius: '4px',
              fontSize: 9,
              fontWeight: 700,
              bgcolor: 'hsl(var(--muted))',
              color: 'hsl(var(--foreground))',
            }}
          >
            {label}
          </Box>
          <Box
            component="span"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              color: 'hsl(var(--foreground))',
              // 幅を狭めても名称を欠かさない（依頼 log_web_issues#299-1）。旧地図配車の
              // 地点カード (V3Plans PlanningsPlace.presenter.tsx:283) も折り返して全文出す。
              // overflowWrap: 区切り文字を持たない長い名称を途中で折り返すため
              minWidth: 0,
              flex: 1,
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              lineHeight: 1.35,
            }}
          >
            {locationName}
          </Box>
        </Box>
        {address && (
          <Box
            data-testid="terminal-address"
            sx={{
              fontSize: 10,
              color: 'hsl(var(--foreground))',
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
              lineHeight: 1.35,
              mt: '2px',
            }}
          >
            {address}
          </Box>
        )}
      </Box>
    </Box>
  );
};

/** 展開パネル。72px の選択行の兄弟として置かれる（行の内側は overflow: hidden で切られる） */
export const ExpandedRowPanel: FC<Props> = ({
  vm, cards, onSelectCard, onReorderStops, reorderDisabledReason, newlyAssignedOrderIds,
}) => {
  // 掴んだ直後の微小な動きでドラッグが始まらないよう距離を置く（車両行と同じ 5px）
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // 保存が返るまでの見た目。debounce で 2 秒待つので、これが無いと掴んだ行が元に戻る
  const [preview, setPreview] = useState<ExpandedStopVM[] | null>(null);
  // 保存が返って VM が入れ替わったら、サーバの並びに戻す
  useEffect(() => setPreview(null), [vm]);
  const stops = preview ?? vm.stops;

  const handleStopDragEnd = useCallback((event: DragEndEvent) => {
    const next = reorderByKey(stops, (s) => s.stopId, event.active.id, event.over?.id);
    if (!next || !onReorderStops) return;
    setPreview(next);
    onReorderStops(next);
  }, [stops, onReorderStops]);

  const planRange = formatPlanRange(vm.planStartAt, vm.planEndAt, vm.planIdleSeconds);

  return (
    <Box
      sx={{
        bgcolor: 'hsl(var(--secondary) / 0.1)',
        borderRadius: '0 0 12px 12px',
        px: 1,
        py: 0.75,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 0.5,
          pb: 0.5,
          borderBottom: '1px solid hsl(var(--border) / 0.4)',
        }}
      >
        {/* 「計画」と所要時間を添える。時刻だけだと何の時刻か読めない（サンプル準拠） */}
        <Box component="span" sx={{ fontSize: 11, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
          {`計画 ${planRange}`}
        </Box>
        <Box component="span" sx={{ fontSize: 11, color: 'hsl(var(--muted-foreground))', whiteSpace: 'nowrap' }}>
          {vm.capacityText}
        </Box>
      </Box>

      {vm.departureTerminal && (
        <TerminalRow
          variant="start"
          locationName={vm.departureTerminal.locationName}
          time={vm.departureTerminal.time ?? '--:--'}
          address={vm.departureTerminal.address}
        />
      )}

      {vm.stops.length === 0 ? (
        // buildOperationPlaces の fail-closed 経路（不一致・曖昧キー・採番不能）を踏むと
        // ヘッダーと容量だけ出て停車行が 0 件になりうる。無言のままだと不具合に見えるため
        // 理由を出す（起きても実害は表示欠落だけなので致命扱いにはしない）
        <Box sx={{ px: 0.75, py: 0.5, fontSize: 11, color: 'hsl(var(--muted-foreground))' }}>
          運行順を取得できませんでした
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={dndModifiers}
          onDragEnd={handleStopDragEnd}
        >
          <SortableContext items={stops.map((s) => s.stopId)} strategy={verticalListSortingStrategy}>
            {stops.map((stop) => {
              const card = cards[stop.cardIndex];
              const row = (dragHandle?: ReactNode) => (
                <ExpandedStopRow
                  stop={stop}
                  address={stop.address ?? addressOf(card, stop.kind)}
                  dragHandle={dragHandle}
                  isNewlyAssigned={
                    !!newlyAssignedOrderIds
                    && stop.shipmentOrderIds.some((id) => newlyAssignedOrderIds.has(id))
                  }
                  // card が引けないときはハンドラを渡さない。no-op を渡すと role="button" と
                  // tabIndex が付いて「押せるのに何も起きない行」になる
                  onClick={card && onSelectCard ? () => onSelectCard(card, stop.dispatchOrderIndex) : undefined}
                />
              );
              // 並べ替えできない画面では sortable にせず、素の行のまま描く
              if (!onReorderStops) return <Fragment key={stop.stopId}>{row()}</Fragment>;
              return (
                <SortableStopRow
                  key={stop.stopId}
                  id={stop.stopId}
                  disabledReason={reorderDisabledReason}
                >
                  {row}
                </SortableStopRow>
              );
            })}
          </SortableContext>
        </DndContext>
      )}

      {/* 出発だけ出て帰りが無いと、最後の納品で終わったように見える。
          最後の回転にだけ出す（1 回転目に出すと 2 回転目の積み降ろしが帰庫の後ろに並ぶ） */}
      {vm.arrivalTerminal && (
        <TerminalRow
          variant="end"
          locationName={vm.arrivalTerminal.locationName}
          time={vm.arrivalTerminal.time ?? hhmm(vm.planEndAt)}
          address={vm.arrivalTerminal.address}
        />
      )}
    </Box>
  );
};
